import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { promptLogin } from "@/lib/authRedirect";
import { toast } from "sonner";
import { Search, Loader2, Plus, Compass, Bookmark, Check, MessageCircle } from "lucide-react";
import SEO from "@/components/site/SEO";
import DiscussionCard from "@/components/site/DiscussionCard";
import ComposeDiscussionModal from "@/components/site/ComposeDiscussionModal";

/**
 * Forum consolidated shell — /forum
 *
 * Three-tab navigation persisted in the URL via `?tab=` + optional `?sort=`:
 *   • semua-ruang  → directory of all 16 Ruang (browse/join)
 *   • diskusi      → cross-Ruang discovery feed, sub-filter Terbaru | Terpopuler | Belum Terjawab
 *   • ruang-anda   → personalized feed of discussions from Ruang the user follows
 *
 * Default tab (only applies when URL has no explicit ?tab= param):
 *   • Guest                      → diskusi
 *   • Logged-in, 0 memberships   → diskusi
 *   • Logged-in, ≥1 membership   → ruang-anda
 *
 * Data sources — no duplicates:
 *   • GET /ruang               → seluruh Ruang + live member_count / topic_count (source of truth for counters)
 *   • GET /me/memberships      → list of joined ruang_ids (used to render "Tergabung ✓" state)
 *   • GET /forum/feed?scope=…  → both diskusi & ruang-anda feed (existing endpoint, now with belum-terjawab)
 */
const TAB = { SEMUA: "semua-ruang", DISKUSI: "diskusi", ANDA: "ruang-anda" };
const SORT = { TERBARU: "topik-terbaru", BALASAN: "balasan-terbaru", BELUM: "belum-terjawab" };
// URL sort keys → backend sort keys. `topik-terbaru` and `balasan-terbaru` are the
// user-facing names; backend continues to accept `terbaru` (now = created_at DESC).
const SORT_TO_API = { "topik-terbaru": "terbaru", "balasan-terbaru": "balasan-terbaru", "belum-terjawab": "belum-terjawab" };

export default function RuangHome() {
  const nav = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [params, setParams] = useSearchParams();

  const urlTab = params.get("tab");
  const urlSort = params.get("sort");

  const [chips, setChips] = useState([]);            // joined ruang list (for Ruang Anda inline chips)
  const [joined, setJoined] = useState(() => new Set());  // ruang_ids the user has joined
  const [groups, setGroups] = useState({ grup_a: [], grup_b: [] }); // Semua Ruang directory
  const [chipsLoaded, setChipsLoaded] = useState(false);
  const [dirLoading, setDirLoading] = useState(true);
  const [pendingRuang, setPendingRuang] = useState(null);

  const [feed, setFeed] = useState(null);
  const [feedLoading, setFeedLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [query, setQuery] = useState("");
  const [composeOpen, setComposeOpen] = useState(false);

  // ---- Default tab resolution ------------------------------------------
  // We ONLY fall back to defaults when the URL doesn't specify a tab.
  // Explicit ?tab=... always wins per acceptance test §16 test 3.
  const validTab = urlTab && Object.values(TAB).includes(urlTab) ? urlTab : null;
  const tab = validTab || (
    // New default rules (Feb 2026):
    //   Guest OR logged-in with zero memberships → Semua Ruang (find communities first).
    //   Logged-in with ≥1 membership → Diskusi (discovery feed) with Topik Terbaru default.
    //   Ruang Anda is no longer a default landing — it's a personalized view users opt into.
    !user ? TAB.SEMUA
      : (chipsLoaded && joined.size >= 1 ? TAB.DISKUSI : TAB.SEMUA)
  );
  const validSort = urlSort && Object.values(SORT).includes(urlSort) ? urlSort : null;
  const sort = validSort || SORT.TERBARU;

  // Persist the effective tab into the URL once we can compute it, so refresh keeps state.
  useEffect(() => {
    if (authLoading) return;
    // Only add a tab to the URL if none exists — never override an explicit tab.
    // Wait for memberships to load before writing so a fresh session lands on the right default.
    if (!urlTab && chipsLoaded) {
      const next = new URLSearchParams(params);
      next.set("tab", tab);
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, urlTab, chipsLoaded, tab]);

  const setTab = (nextTab) => {
    const next = new URLSearchParams(params);
    next.set("tab", nextTab);
    // Drop sort param when leaving Diskusi so it doesn't leak to other tabs.
    if (nextTab !== TAB.DISKUSI) next.delete("sort");
    setParams(next);
    try { window.dataLayer && window.dataLayer.push({ event: "feed_tab_switch", tab: nextTab }); } catch { /* noop */ }
  };

  const setSort = (nextSort) => {
    const next = new URLSearchParams(params);
    next.set("tab", TAB.DISKUSI);
    next.set("sort", nextSort);
    setParams(next);
  };

  // ---- Directory load (Semua Ruang) ------------------------------------
  useEffect(() => {
    let alive = true;
    (async () => {
      setDirLoading(true);
      try {
        const [rGroups, rMem] = await Promise.all([
          api.get("/ruang"),
          user ? api.get("/me/memberships").catch(() => ({ data: { items: [], ruang_ids: [] } })) : Promise.resolve({ data: { items: [], ruang_ids: [] } }),
        ]);
        if (!alive) return;
        setGroups({ grup_a: rGroups.data.grup_a?.items || [], grup_b: rGroups.data.grup_b?.items || [] });
        setChips(rMem.data.items || []);
        setJoined(new Set(rMem.data.ruang_ids || (rMem.data.items || []).map((i) => i.ruang_id)));
      } catch {
        toast.error("Gagal memuat daftar Ruang.");
      } finally {
        if (alive) { setDirLoading(false); setChipsLoaded(true); }
      }
    })();
    return () => { alive = false; };
  }, [user]);

  // ---- Feed load (Diskusi + Ruang Anda) --------------------------------
  const fetchFeed = useCallback(async (currentTab, currentSort, cursor = null) => {
    // Semua Ruang doesn't need the feed; skip.
    if (currentTab === TAB.SEMUA) return;
    // Guest hitting Ruang Anda: don't call the auth-required endpoint at all —
    // render the login-prompt empty state instead. This preserves URL state
    // (?tab=ruang-anda) so browser back/forward keeps working.
    const p = new URLSearchParams();
    if (currentTab === TAB.ANDA) {
      p.set("scope", "mine");
    } else {
      p.set("scope", "all");
      p.set("sort", SORT_TO_API[currentSort] || "terbaru");
    }
    p.set("limit", "20");
    if (cursor) p.set("cursor", cursor);
    try {
      const r = await api.get(`/forum/feed?${p.toString()}`);
      if (cursor) {
        setFeed((f) => f ? { ...r.data, items: [...(f?.items || []), ...(r.data.items || [])] } : r.data);
      } else {
        setFeed(r.data);
      }
    } catch (e) {
      // 401 on mine (guest) → show empty state, don't redirect (keeps nav persistence).
      if (currentTab === TAB.ANDA && e?.response?.status === 401) {
        setFeed({ items: [], next_cursor: null, requires_auth: true });
        return;
      }
      toast.error("Gagal memuat feed.");
      if (!cursor) setFeed({ items: [], next_cursor: null });
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (tab === TAB.SEMUA) return;
    setFeedLoading(true);
    setFeed(null);
    fetchFeed(tab, sort).finally(() => setFeedLoading(false));
  }, [tab, sort, authLoading, fetchFeed]);

  const loadMore = async () => {
    if (!feed?.next_cursor || loadingMore) return;
    setLoadingMore(true);
    await fetchFeed(tab, sort, feed.next_cursor);
    setLoadingMore(false);
  };

  // ---- Directory join/leave toggle -------------------------------------
  const toggleJoin = async (r) => {
    if (!user) { promptLogin(nav); return; }
    const isJoined = joined.has(r.ruang_id);
    setPendingRuang(r.ruang_id);
    // Optimistic membership + counter update (counter reverts if server errors).
    setJoined((prev) => {
      const nx = new Set(prev);
      isJoined ? nx.delete(r.ruang_id) : nx.add(r.ruang_id);
      return nx;
    });
    setGroups((g) => ({
      grup_a: g.grup_a.map((x) => x.ruang_id === r.ruang_id ? { ...x, member_count: Math.max(0, (x.member_count || 0) + (isJoined ? -1 : 1)) } : x),
      grup_b: g.grup_b.map((x) => x.ruang_id === r.ruang_id ? { ...x, member_count: Math.max(0, (x.member_count || 0) + (isJoined ? -1 : 1)) } : x),
    }));
    try {
      await api.post(`/ruang/${r.ruang_id}/${isJoined ? "leave" : "join"}`);
    } catch (e) {
      setJoined((prev) => {
        const nx = new Set(prev);
        isJoined ? nx.add(r.ruang_id) : nx.delete(r.ruang_id);
        return nx;
      });
      setGroups((g) => ({
        grup_a: g.grup_a.map((x) => x.ruang_id === r.ruang_id ? { ...x, member_count: Math.max(0, (x.member_count || 0) + (isJoined ? 1 : -1)) } : x),
        grup_b: g.grup_b.map((x) => x.ruang_id === r.ruang_id ? { ...x, member_count: Math.max(0, (x.member_count || 0) + (isJoined ? 1 : -1)) } : x),
      }));
      toast.error(e?.response?.data?.detail || "Gagal memperbarui keanggotaan.");
    } finally { setPendingRuang(null); }
  };

  // ---- Feed filter search ---------------------------------------------
  // Compute `items` inside the memo so its identity doesn't change every
  // render (satisfies react-hooks/exhaustive-deps at error level).
  const filtered = useMemo(() => {
    const items = feed?.items || [];
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((d) =>
      (d.title || "").toLowerCase().includes(q) ||
      (d.body_excerpt || "").toLowerCase().includes(q) ||
      (d.ruang_name || "").toLowerCase().includes(q)
    );
  }, [feed?.items, query]);
  const items = feed?.items || [];

  const onComposed = (newDiscussion) => {
    setFeed((f) => f ? { ...f, items: [{ ...newDiscussion, is_joined: true }, ...f.items] } : f);
  };

  // ---- Rendering guards -----------------------------------------------
  if (authLoading || (!chipsLoaded && !urlTab)) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-brand-alabaster">
        <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-alabaster" data-testid="ruang-home-page">
      <SEO title="Ruang Diskusi — The School Reports" description="Jelajahi Ruang parenting, diskusi lintas komunitas, dan feed personal Anda." />

      <div className="max-w-3xl mx-auto px-5 pt-8 pb-16">
        {/* Hero — fixed identity, no per-tab change */}
        <p className="eyebrow text-brand-primary mb-2" data-testid="forum-eyebrow">PARENTS DISCUSSION FORUM</p>
        <h1 className="font-serif-display text-3xl md:text-4xl text-brand-primary leading-[1.15] mb-2" data-testid="forum-heading">
          Ruang Anda, obrolan hari ini
        </h1>
        <p className="text-brand-text-muted text-sm mb-5" data-testid="forum-subtitle">
          Update terbaru dari Ruang yang Anda ikuti.
        </p>

        {/* 3-tab segmented control — moved up per new header hierarchy */}
        <div
          className="grid grid-cols-3 gap-1 p-1 rounded-full bg-[#F3F1EC]"
          role="tablist"
          data-testid="feed-tabs"
        >
          {[
            { k: TAB.SEMUA, label: "Semua Ruang" },
            { k: TAB.DISKUSI, label: "Diskusi" },
            { k: TAB.ANDA, label: "Ruang Anda" },
          ].map((t) => {
            const on = tab === t.k;
            return (
              <button
                key={t.k}
                role="tab"
                aria-selected={on}
                onClick={() => setTab(t.k)}
                data-testid={`feed-tab-${t.k}`}
                className={`h-10 rounded-full text-sm transition-all ${
                  on
                    ? "bg-white text-[#14294D] font-semibold shadow-sm border border-brand-accent"
                    : "text-[#8B8B90] hover:text-brand-text border border-transparent"
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Context hint immediately below segmented navigation */}
        <p className="text-sm text-brand-text-muted mt-3 mb-5" data-testid="tab-context-hint">
          Anda di{" "}
          <span className="font-semibold text-brand-primary">
            {tab === TAB.SEMUA ? "Semua Ruang" : tab === TAB.DISKUSI ? "Diskusi" : "Ruang Anda"}
          </span>{" "}
          —{" "}
          {tab === TAB.SEMUA && "daftar Ruang untuk Anda gabung."}
          {tab === TAB.DISKUSI && "obrolan terbaru lintas semua Ruang."}
          {tab === TAB.ANDA && "feed dari Ruang yang Anda ikuti."}
        </p>

        {/* Action row: CTA | Search (flex-grow) | Bookmark.
            Mobile fallback (<640px): search jumps to its own row so CTA doesn't
            get compressed and the field stays usable. */}
        <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="forum-action-row">
          <button
            onClick={() => (user ? setComposeOpen(true) : promptLogin(nav))}
            data-testid="ruang-home-compose-btn"
            className="order-1 h-12 px-6 rounded-full bg-brand-accent hover:bg-brand-accent-hover text-white font-semibold shadow-sm transition-colors basis-[calc(100%-4rem)] sm:basis-auto sm:min-w-[210px] whitespace-nowrap"
          >
            + Mulai diskusi
          </button>
          <div className="relative order-3 sm:order-2 flex-1 basis-full sm:basis-0 min-w-0">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-text-muted" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cari diskusi, sekolah, atau topik…"
              data-testid="ruang-home-search"
              className="w-full h-12 rounded-full bg-[#F3F1EC] border border-transparent pl-11 pr-4 text-sm focus:border-brand-primary focus:bg-white focus:outline-none transition-colors"
            />
          </div>
          <Link
            to="/me/bookmarks"
            data-testid="ruang-home-bookmarks-link"
            aria-label="Diskusi tersimpan"
            className="order-2 sm:order-3 w-12 h-12 rounded-full bg-white border border-brand-border flex items-center justify-center hover:border-brand-accent hover:text-brand-accent text-[#8B8B90] transition-colors shrink-0"
          >
            <Bookmark className="w-5 h-5" strokeWidth={1.75} />
          </Link>
        </div>

        {/* Sub-filter for Diskusi tab — underline style, subordinate to primary nav */}
        {tab === TAB.DISKUSI && (
          <div className="flex items-center gap-6 mb-4 overflow-x-auto no-scrollbar" data-testid="feed-subfilter">
            {[
              { k: SORT.TERBARU, label: "Topik Terbaru" },
              { k: SORT.BALASAN, label: "Balasan Terbaru" },
              { k: SORT.BELUM, label: "Belum Terjawab" },
            ].map(({ k, label }) => {
              const on = sort === k;
              return (
                <button
                  key={k}
                  onClick={() => setSort(k)}
                  data-testid={`feed-sort-${k}`}
                  className={`pb-1.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    on ? "border-brand-accent text-brand-primary font-semibold" : "border-transparent text-[#8B8B90] hover:text-brand-text"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}

        {/* --------------- Panel: Semua Ruang --------------- */}
        {tab === TAB.SEMUA && (
          <div className="mt-6" data-testid="panel-semua-ruang">
            {dirLoading ? (
              <div className="py-12 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand-primary" /></div>
            ) : (
              <div className="space-y-8">
                <RuangGroupSection
                  title="Sekolah"
                  subtitle="SESUAI JENJANG"
                  items={groups.grup_a}
                  joined={joined}
                  pending={pendingRuang}
                  onToggle={toggleJoin}
                  query={query}
                />
                <RuangGroupSection
                  title="Pendidikan & Kehidupan Anak"
                  items={groups.grup_b}
                  joined={joined}
                  pending={pendingRuang}
                  onToggle={toggleJoin}
                  query={query}
                />
              </div>
            )}
          </div>
        )}

        {/* --------------- Panel: Diskusi + Ruang Anda --------------- */}
        {(tab === TAB.DISKUSI || tab === TAB.ANDA) && (
          <>
            {/* Ruang Anda section eyebrow */}
            {tab === TAB.ANDA && chipsLoaded && joined.size > 0 && !feed?.requires_auth && (
              <p className="eyebrow text-brand-primary mt-6 mb-3" data-testid="ruang-anda-eyebrow">TERBARU DI RUANG ANDA</p>
            )}

            {/* Ruang Anda empty state — 0 memberships OR guest (requires_auth) */}
            {tab === TAB.ANDA && chipsLoaded && (joined.size === 0 || feed?.requires_auth) && (
              <div className="mt-6 rounded-2xl bg-white border border-brand-border p-8 text-center shadow-sm" data-testid="ruang-home-empty-onboarding">
                <div className="w-14 h-14 rounded-full bg-brand-muted flex items-center justify-center mx-auto mb-4">
                  <Compass className="w-7 h-7 text-brand-primary" strokeWidth={1.5} />
                </div>
                <h3 className="font-sans font-bold text-lg text-[#1C1C1F] mb-2">
                  {feed?.requires_auth ? "Masuk untuk melihat Ruang Anda" : "Beranda Anda masih kosong"}
                </h3>
                <p className="text-sm text-[#4A4A4F] mb-5 max-w-sm mx-auto leading-relaxed">
                  {feed?.requires_auth
                    ? "Ruang Anda menampilkan feed personal dari Ruang yang Anda ikuti. Masuk untuk melanjutkan."
                    : <>Anda belum mengikuti Ruang apapun. Gabung Ruang di tab <strong>Semua Ruang</strong> untuk melihat feed personal Anda.</>}
                </p>
                {feed?.requires_auth ? (
                  <button
                    onClick={() => promptLogin(nav)}
                    data-testid="ruang-home-login-cta"
                    className="inline-flex items-center justify-center h-11 px-6 rounded-full bg-brand-accent hover:bg-brand-accent-hover text-white font-semibold text-sm transition-colors"
                  >
                    Masuk
                  </button>
                ) : (
                  <button
                    onClick={() => setTab(TAB.SEMUA)}
                    data-testid="ruang-home-jelajah-cta"
                    className="inline-flex items-center justify-center h-11 px-6 rounded-full bg-brand-accent hover:bg-brand-accent-hover text-white font-semibold text-sm transition-colors"
                  >
                    Jelajah Semua Ruang
                  </button>
                )}
              </div>
            )}

            {/* Feed */}
            {!(tab === TAB.ANDA && chipsLoaded && (joined.size === 0 || feed?.requires_auth)) && (
              <div className="mt-5" data-testid={`panel-${tab}`}>
                {feedLoading ? (
                  <div className="py-12 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand-primary" /></div>
                ) : (
                  <div className="space-y-4" data-testid="feed-list">
                    {filtered.length === 0 ? (
                      <div className="rounded-2xl bg-white border border-brand-border p-8 text-center shadow-sm" data-testid="feed-empty">
                        <p className="text-brand-text-muted">
                          {query ? "Tidak ada diskusi yang cocok dengan pencarianmu."
                            : (tab === TAB.DISKUSI && sort === SORT.BELUM
                              ? "Tidak ada diskusi yang belum terjawab saat ini."
                              : "Belum ada diskusi. Jadilah yang pertama memulai ✍️")}
                        </p>
                      </div>
                    ) : (
                      filtered.map((d) => <DiscussionCard key={d.discussion_id} d={d} />)
                    )}
                    {feed?.next_cursor && (
                      <button
                        onClick={loadMore}
                        disabled={loadingMore}
                        data-testid="feed-load-more"
                        className="w-full h-11 rounded-full bg-white border border-brand-border text-brand-primary text-sm font-semibold hover:border-brand-primary/40 transition-colors disabled:opacity-50"
                      >
                        {loadingMore ? "Memuat…" : "Muat lebih banyak"}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <ComposeDiscussionModal open={composeOpen} onClose={() => setComposeOpen(false)} onCreated={onComposed} />
    </div>
  );
}

// ---------- Semua Ruang directory sub-components ----------
function RuangGroupSection({ title, subtitle, items, joined, pending, onToggle, query }) {
  const filtered = useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    if (!q) return items;
    return items.filter((r) => (r.name || "").toLowerCase().includes(q) || (r.description || "").toLowerCase().includes(q));
  }, [items, query]);
  if (filtered.length === 0 && query) return null;
  return (
    <section data-testid={`semua-ruang-group-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-baseline justify-between mb-4">
        <p className="eyebrow text-brand-primary">
          {title.toUpperCase()}{subtitle && <span className="text-brand-text-muted"> — {subtitle}</span>}
        </p>
        <span className="eyebrow text-brand-text-muted">{items.length} RUANG</span>
      </div>
      <div className="grid grid-cols-2 gap-2.5 md:gap-3.5">
        {filtered.map((r) => (
          <RuangCard
            key={r.ruang_id}
            r={r}
            isJoined={joined.has(r.ruang_id)}
            pending={pending === r.ruang_id}
            onToggle={() => onToggle(r)}
          />
        ))}
      </div>
    </section>
  );
}

function RuangCard({ r, isJoined, pending, onToggle }) {
  return (
    <div
      className="flex flex-col gap-2.5 rounded-2xl bg-white border border-brand-border p-3.5 md:p-4 hover:border-brand-primary/30 transition-colors min-w-0"
      data-testid={`semua-ruang-card-${r.slug}`}
    >
      <Link to={`/forum/ruang/${r.slug}`} className="flex flex-col gap-1.5 min-w-0 group" data-testid={`semua-ruang-link-${r.slug}`}>
        <span className="w-10 h-10 md:w-11 md:h-11 rounded-xl bg-brand-muted flex items-center justify-center text-xl md:text-2xl shrink-0" aria-hidden>
          {r.icon}
        </span>
        <span className="block font-semibold text-brand-text text-sm md:text-[15px] leading-snug line-clamp-2 group-hover:underline underline-offset-2 mt-1">{r.name}</span>
        <span className="inline-flex items-center gap-1 text-[11px] md:text-xs text-brand-text-muted" data-testid={`semua-ruang-count-${r.slug}`}>
          <MessageCircle className="w-3 h-3" strokeWidth={1.75} />
          {formatCount(r.topic_count ?? r.discussion_count ?? 0)} topik · {formatCount(r.member_count)} anggota
        </span>
      </Link>
      <button
        onClick={onToggle}
        disabled={pending}
        data-testid={`semua-ruang-toggle-${r.slug}`}
        className={`h-9 rounded-full text-xs md:text-sm font-semibold transition-all whitespace-nowrap ${
          isJoined
            ? "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
            : "bg-brand-accent/5 text-brand-accent border border-brand-accent/30 hover:bg-brand-accent hover:text-white hover:border-brand-accent"
        } disabled:opacity-50`}
      >
        {isJoined ? (
          <span className="inline-flex items-center justify-center gap-1"><Check className="w-3.5 h-3.5" strokeWidth={3} /> Tergabung</span>
        ) : (
          "Gabung"
        )}
      </button>
    </div>
  );
}

function formatCount(n) {
  const num = Number(n || 0);
  if (num >= 1000) return `${(num / 1000).toFixed(1).replace(/\.0$/, "")}rb`;
  return String(num);
}
